import * as React from 'react';
import {faMinus, faPlus} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Ref, useEffect, useState} from 'react';

import Button from '../../Buttons/Button';

export const stepperErrorInitialState: StepperError = {error: ``, message: ``};

export interface StepperError {
    error: string;
    message: string;
}

interface StepperProps {
    className?: string;
    containerClass?: string;
    disabled?: boolean;
    e2e?: string;
    error?: string;
    inputClass?: string;
    large?: boolean;
    maximum?: number;
    minimum?: number;
    name?: string;
    onError?: (stepperError: StepperError) => void;
    onUpdate?: (val) => void;
    onUpdateAllowInvalid?: boolean;
    ref?: Ref<unknown> | null;
    smStepperFull?: boolean;
    stepperLg?: boolean;
    value?: number;
}

export const StepperComponent = (
    {
        className,
        containerClass,
        disabled,
        e2e,
        error,
        inputClass,
        large = false,
        maximum = 9999,
        minimum = 1,
        name,
        onError,
        onUpdate,
        onUpdateAllowInvalid,
        smStepperFull = false,
        stepperLg = false,
        value = 1,
    }: StepperProps,
    ref,
) => {
    const [stepperErrorState, setStepperErrorState] = useState<StepperError>(stepperErrorInitialState);
    const [stepperValue, setStepperValue] = useState(value);

    // If the value is changed externally, update internal value
    useEffect(() => {
        setStepperValue(value);
    }, [value]);

    // minimum / maximum Validation Handling
    useEffect(() => {
        if (!isNaN(stepperValue) && (stepperValue !== value || stepperErrorState !== stepperErrorInitialState)) {
            if (stepperValue > maximum) {
                const stepperError = {error: `Limit ${maximum}`, message: `Order quantity limit exceeded`};
                setStepperErrorState(stepperError);
                if (onUpdateAllowInvalid && onUpdate) {
                    onUpdate(stepperValue);
                }
                if (onError) {
                    onError(stepperError);
                }
            } else if (stepperValue < minimum) {
                const stepperError = {error: `${minimum} required`, message: `Set order quantity to ${minimum} or greater`};
                setStepperErrorState(stepperError);
                if (onUpdateAllowInvalid && onUpdate) {
                    onUpdate(stepperValue);
                }
                if (onError) {
                    onError(stepperError);
                }
            } else {
                setStepperErrorState(stepperErrorInitialState);
                if (onError) {
                    onError(stepperErrorInitialState);
                }
                if (stepperValue !== value && onUpdate) {
                    onUpdate(stepperValue);
                }
            }
        }
        // We only want stepperValue to trigger changes in this hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepperValue]);

    /**
     * Template
     */
    return (
        <div className={`${containerClass ? ' ' + containerClass : ''}`}>
            <div
                className={`${
                    large ? '[&_input]:!tw-h-[52px] [&_input]:!tw-w-[52px] [&_button]:!tw-h-[52px] [&_button]:!tw-w-[52px]' : ''
                } ${className}`}
            >
                <div className="incrementer tw-relative tw-flex tw-items-stretch tw-w-full">
                    <div className={`${large ? '' : 'tw-h-[38px]'} tw-mr-[-1px] tw-flex`}>
                        <Button
                            className={`!tw-z-auto !tw-border-gray-200 hover:!tw-bg-white focus:!tw-bg-white focus:!tw-border-gray-700 !tw-h-[38px] !tw-w-[38px]${
                                stepperLg ? ' !tw-h-[52px] !tw-w-[52px]' : ' !tw-px-2'
                            }`}
                            disabled={disabled || stepperValue <= minimum}
                            onClick={() => {
                                setStepperValue(stepperValue - 1);
                            }}
                            variant={disabled || stepperValue <= minimum ? `outline-light` : `outline-secondary`}
                        >
                            <FontAwesomeIcon
                                className={`${disabled || stepperValue <= minimum ? 'tw-text-gray-400' : 'tw-text-gray-650'} ${
                                    large ? 'tw-align-middle' : ''
                                } `}
                                icon={faMinus}
                            />
                        </Button>
                    </div>
                    <input
                        className={`!tw-z-auto tw-text-base !tw-h-[38px] tw-w-[38px] tw-min-w-[38px]${
                            large ? ' tw-text-xl' : ''
                        } tw-font-normal tw-leading-normal tw-z-[3] tw-text-gray-700 tw-border tw-text-center tw-mb-0 tw-relative tw-p-0 ${
                            error ? ' tw-border-red-500' : ''
                        }${
                            stepperLg
                                ? ' !tw-h-[52px] !tw-w-full !tw-flex !tw-flex-grow'
                                : `${smStepperFull ? '!tw-w-full !tw-flex' : 'tw-flex-none'}`
                        }${
                            disabled || (stepperValue === maximum && stepperValue === minimum)
                                ? ' tw-bg-neutral-100 !tw-text-gray-300'
                                : ' tw-bg-white'
                        }${inputClass ? ` ${inputClass}` : ''}`}
                        data-e2e={e2e}
                        disabled={disabled || (stepperValue === maximum && stepperValue === minimum)}
                        inputMode="numeric"
                        maxLength={3}
                        name={name}
                        onChange={(e) => {
                            setStepperValue(parseInt(e.target.value));
                        }}
                        pattern="[0-9]+"
                        ref={ref}
                        size={5}
                        type="number"
                        value={stepperValue}
                    />
                    <div className="tw-flex tw-ml-[-1px]">
                        <Button
                            className={`!tw-z-auto !tw-border-gray-200 hover:!tw-bg-white focus:!tw-bg-white focus:!tw-border-gray-700 !tw-h-[38px] !tw-w-[38px]${
                                large ? ' hover:!tw-bg-transparent focus:tw-bg-transparent' : ' !tw-px-2'
                            }${stepperLg ? ' !tw-h-[52px] !tw-w-[52px]' : ''}`}
                            disabled={disabled || stepperValue >= maximum}
                            onClick={() => {
                                setStepperValue(stepperValue + 1);
                            }}
                            variant={disabled || stepperValue >= maximum ? `outline-light` : `outline-secondary`}
                        >
                            <FontAwesomeIcon
                                className={`${disabled || stepperValue >= maximum ? 'tw-text-gray-400' : 'tw-text-gray-650'} ${
                                    large ? 'tw-align-middle tw-text-2xl' : ''
                                } `}
                                icon={faPlus}
                            />
                        </Button>
                    </div>
                </div>
                <div className="tw-flex tw-justify-between">
                    {error && <div className="tw-text-red-500 tw-text-sm tw-leading-[1.3] tw-font-normal">{error}</div>}
                </div>
            </div>
        </div>
    );
};
export const Stepper = React.forwardRef(StepperComponent);
